import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { MobilePreviewContext } from '../../context/MobilePreviewContext';
import CellularIcon from './assets/Cellular.svg?react';
import WifiIcon from './assets/Wifi.svg?react';
import BatteryIcon from './assets/Percentage.svg?react';
import styles from './MobilePreviewFrame.module.css';

const MOBILE_VIEW_PARAM = 'viewMode';

const formatTime = () => {
  const now = new Date();
  return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
};

let currentTime = formatTime();
const timeListeners = new Set<() => void>();
setInterval(() => {
  const next = formatTime();
  if (next !== currentTime) {
    currentTime = next;
    timeListeners.forEach((l) => l());
  }
}, 1000);

const useCurrentTime = () =>
  useSyncExternalStore(
    (cb) => { timeListeners.add(cb); return () => timeListeners.delete(cb); },
    () => currentTime,
  );

type MobilePreviewFrameProps = {
  children: ReactNode;
};

export const MobilePreviewFrame = ({ children }: MobilePreviewFrameProps) => {
  const isActive = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get(MOBILE_VIEW_PARAM) === 'mobile';
  }, []);

  const time = useCurrentTime();
  const screenRef = useRef<HTMLDivElement>(null);
  const [screenEl, setScreenEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setScreenEl(screenRef.current);
  }, []);

  const handlePointerUp = useCallback((e: PointerEvent) => {
    const screen = screenRef.current;
    if (!screen) return;
    if (screen.contains(e.target as Node)) return;

    const canvas = screen.querySelector('canvas');
    if (!canvas) return;

    canvas.dispatchEvent(
      new PointerEvent('pointerup', {
        bubbles: true,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        clientX: e.clientX,
        clientY: e.clientY,
      }),
    );
  }, []);

  useEffect(() => {
    if (!isActive) return;
    document.addEventListener('pointerup', handlePointerUp);
    return () => document.removeEventListener('pointerup', handlePointerUp);
  }, [isActive, handlePointerUp]);

  if (!isActive) {
    return <>{children}</>;
  }

  return (
    <div className={styles.backdrop}>
      <div className={styles.device}>
        <div
          ref={screenRef}
          className={styles.screen}
          style={{
            '--safe-top': '54px',
            '--safe-right': '0px',
            '--safe-bottom': '0px',
            '--safe-left': '0px',
            '--preview-safe-bottom': '24px',
            '--keyboard-bottom-offset': '0px',
          } as React.CSSProperties}
        >
          <MobilePreviewContext.Provider value={screenEl}>
            {children}
          </MobilePreviewContext.Provider>
        </div>
        <img
          src="/iphone-frame.png"
          alt=""
          className={styles.frameOverlay}
          draggable={false}
        />
        <div className={styles.statusBar}>
          <span className={styles.statusTime}>{time}</span>
          <div className={styles.statusRight}>
            <CellularIcon className={styles.statusIcon} />
            <WifiIcon className={styles.statusIcon} />
            <BatteryIcon className={styles.statusIcon} />
          </div>
        </div>
      </div>
    </div>
  );
};
